import React, { useRef, useState, useEffect, useCallback } from 'react';
import anime from 'animejs';
import './drawer.css';

type TPosition = 'left' | 'right' | 'top' | 'bottom';

export interface IDrawer {
  className?: string;
  // The content, not necessary if declared inside the tag
  children?: any;
  // Overlay background color
  backgroundColor?: any;
  // Drawer size (width or height)
  size?: number;
  // Minimum size if resizeable
  minSize?: number;
  // Maximum size if resizeable
  maxSize?: number;
  // The drawer opened status
  open?: boolean;
  // Enables resizing
  resizeable?: boolean;
  // Drawer position on screen
  position?: TPosition;
  // Event fired when resized
  onChange?: (size: number) => void;
  // Event fired when starting to open
  onBeginOpen?: () => void;
  // Event fired when completed the opened state
  onCompleteOpen?: () => void;
  // Event fired when starting to close
  onBeginClose?: () => void;
  // Event fired when completed the closed state
  onCompleteClose?: () => void;
  // Outside click closed drawer
  onCloseOutsideClick?: boolean;
}

function useEventListener(
  eventName: string,
  handler: any,
  element = window as any,
  passive = false,
) {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) return;

    const eventListener = (event: any) => {
      if (!savedHandler.current) {
        return;
      }
      return savedHandler.current(event);
    };

    element.addEventListener(eventName, eventListener, { passive });

    return () => {
      element.removeEventListener(eventName, eventListener, { passive });
    };
  }, [eventName, element, passive]);
}

const classesFromObject = (classes: any, additionalClassName?: string) => {
  const availableClasses = [
    ...Object.keys(classes).filter((cls?: string) => cls && !!classes[cls]),
    ...[additionalClassName],
  ];

  return availableClasses.join(' ');
};

const Drawer = (props: IDrawer) => {
  const uid: any = `inguide-Drawer`;
  const {
    className: propClassName,
    children,
    backgroundColor = 'light',
    size: propSize = 200,
    minSize = 100,
    maxSize = 350,
    open = false,
    resizeable = false,
    position = 'right',
    onChange = () => null,
    onBeginOpen = () => null,
    onCompleteOpen = () => null,
    onBeginClose = () => null,
    onCompleteClose = () => null,
    onCloseOutsideClick,
  } = props;

  const controlRef = useRef(null);
  const contentRef: any = useRef(null);
  const bgRef = useRef(null);

  const [isResizing, setIsResizing] = useState(false);
  const [isOpen, setIsOpen] = useState(open);
  const [isOpening, setIsOpening] = useState(false);
  const [startSize, setStartSize] = useState(0);
  const [startX, setStartX] = useState(0);
  const [drawerSize, setDrawerSize] = useState(propSize);

  const resizeStart = useCallback(() => {
    if (!resizeable) {
      return;
    }

    const sideRect: any = contentRef.current?.getBoundingClientRect();
    const horizontal = ['left', 'right'].includes(position);
    setStartSize(horizontal ? sideRect.width : sideRect.height);
    setStartX(horizontal ? sideRect.x : sideRect.y);
    setIsResizing(true);
  }, [position, resizeable]);

  const resize = useCallback(
    (ev: any) => {
      if (isResizing && resizeable) {
        const newSize = calculateSize({ position, ev, startX, startSize });

        if (newSize < minSize) {
          setDrawerSize(minSize);
        } else if (newSize > maxSize) {
          setDrawerSize(maxSize);
        } else {
          setDrawerSize(newSize);
        }
      }
    },
    [isResizing, maxSize, minSize, position, resizeable, startSize, startX],
  );

  const resizeEnd = useCallback(() => {
    if (isResizing && resizeable) {
      setIsResizing(false);
      onChange(drawerSize);
    }
  }, [isResizing, onChange, resizeable, drawerSize]);

  const toggleOpen = useCallback(
    (op) => {
      const commonProps = {
        duration: 500,
        easing: 'easeOutCirc',
      };

      const { openProps: opr, closeProps: cpr } = positionValues(position);

      const openProps = {
        ...commonProps,
        ...opr,
        begin: () => {
          onBeginOpen();
          setIsOpening(true);
        },
        complete: () => {
          onCompleteOpen();
          setIsOpening(false);
          setIsOpen(true);
        },
      };

      const closeProps = {
        ...commonProps,
        ...cpr,
        begin: onBeginClose,
        complete: () => {
          onCompleteClose();
          setIsOpen(false);
        },
      };

      anime({
        ...commonProps,
        targets: bgRef.current,
        opacity: op ? 1 : 0,
      });

      if (op) {
        anime({
          ...openProps,
          targets: contentRef.current,
        });
      } else {
        anime({
          ...closeProps,
          targets: contentRef.current,
        });
      }
    },
    [onBeginClose, onBeginOpen, onCompleteClose, onCompleteOpen, position],
  );

  useEffect(() => {
    const { openProps: opr, closeProps: cpr } = positionValues(position);
    const vals = open ? opr : cpr;

    anime({
      ...vals,
      targets: contentRef.current,
    });
    //eslint-disable-next-line
  }, [position]);

  useEffect(() => {
    toggleOpen(open);

    //eslint-disable-next-line
  }, [open]);

  useEventListener('mouseup', resizeEnd);

  const classes = {
    'inguide-Drawer': true,
    open: isOpen,
    resizeable: resizeable,
    resizing: isResizing,
    fixed: !resizeable,
    closed: !isOpen,
    opening: isOpening,
    [`side-${position}`]: position,
  };

  const overlayClasses = {
    'bg-overlay': true,
    [backgroundColor]: true,
  };

  const classNames = classesFromObject(classes, propClassName);
  const overlayClassNames = classesFromObject(overlayClasses);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Hook useEffect para monitorar a largura da tela
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth); // Atualiza a largura da tela
    };

    // Adiciona o event listener para mudanças de tamanho da janela
    window.addEventListener('resize', handleResize);

    // Limpa o event listener quando o componente for desmontado
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Ajuste no estilo da drawer
  const drawerStyle: React.CSSProperties = {
    width:
      windowWidth < 710
        ? '100%'
        : ['left', 'right'].includes(position)
        ? drawerSize
        : undefined,
    height: '100%',
    position: 'fixed', // Mudar para fixed
    top: 0,
    bottom: 0,
    left: 'auto',
    background: '#fff',
    right: 0,
    zIndex: 10000, // Z-index para garantir que fique em cima dos outros elementos
    transform: 'translateZ(0)', // Pode ajudar a garantir que o componente esteja "no topo"
  };

  // Estilo do overlay
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999, // Overlay fica abaixo da drawer
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Cor do fundo
  };

  return (
    <div id={uid} className={classNames} onMouseMove={resize}>
      <div
        className={overlayClassNames}
        ref={bgRef}
        onClick={() => (onCloseOutsideClick ? toggleOpen(false) : null)}
        style={overlayStyle}
      ></div>
      <div className="main-content" ref={contentRef} style={drawerStyle}>
        <div
          className="control"
          ref={controlRef}
          onDrag={dragPD}
          onDragStart={dragPD}
          onDragEnd={dragPD}
          onMouseDown={resizeStart}
        />
        <div style={{ height: '100%' }}>{children}</div>
      </div>
    </div>
  );
};

const dragPD = (ev: any) => {
  ev.preventDefault();
};

const calculateSize = ({
  position,
  ev,
  startX,
  startSize,
}: {
  position: TPosition;
  ev: React.MouseEvent<HTMLDivElement>;
  startX: number;
  startSize: number;
}) => {
  switch (position) {
    case 'left':
      return ev.clientX - startX;
    case 'right':
      return startSize + (startX - ev.clientX);
    case 'top':
      return ev.clientY - startX;
    case 'bottom':
      return startSize + (startX - ev.clientY);
  }
};

const positionValues = (position: string) => {
  const openProps = {
    translateX: '0',
    translateY: '0',
  };

  const closeProps = {
    translateX: '0',
    translateY: '0',
  };

  switch (position) {
    case 'left':
      openProps.translateX = '0%';
      closeProps.translateX = '-100%';
      break;
    case 'right':
      openProps.translateX = '0%';
      closeProps.translateX = '100%';
      break;
    case 'top':
      openProps.translateY = '0%';
      closeProps.translateY = '-100%';
      break;
    case 'bottom':
      openProps.translateY = '0%';
      closeProps.translateY = '100%';
      break;
  }

  return { openProps, closeProps };
};

export default Drawer;
